import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Eye, EyeOff, Building2 } from 'lucide-react';
import api from '../../../api.js';

function UserFormModal({ isOpen, onClose, onSuccess, onMessage, editingUser, organizations }) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    role: 'manager',
    primary_org_id: '',
    phone: '',
    status: 'active',
  });
  const [showPassword, setShowPassword] = useState(false);

  // Filiales accessibles (chantier Groupe multi-filiales) — au-delà de l'organisation
  // principale ci-dessus. `grantIdByCode` mémorise l'id de l'octroi existant pour
  // pouvoir le révoquer sans le re-lister à l'enregistrement.
  const [accessibleOrgCodes, setAccessibleOrgCodes] = useState([]);
  const [grantIdByCode, setGrantIdByCode] = useState({});
  const [orgAccessLoading, setOrgAccessLoading] = useState(false);

  useEffect(() => {
    if (editingUser) {
      setFormData({
        email: editingUser.email || '',
        password: '', // Never pre-fill password
        first_name: editingUser.first_name || '',
        last_name: editingUser.last_name || '',
        role: editingUser.role || 'fleet_manager',
        primary_org_id: editingUser.primary_org_id || '',
        phone: editingUser.phone || '',
        status: editingUser.status || 'active',
      });
    } else {
      // Reset form for new user
      setFormData({
        email: '',
        password: '',
        first_name: '',
        last_name: '',
        role: 'manager',
        primary_org_id: '',
        phone: '',
        status: 'active',
      });
    }

    // Charger les filiales déjà accordées (édition seulement — un nouvel
    // utilisateur n'a pas encore d'id à interroger).
    if (editingUser?.id) {
      setOrgAccessLoading(true);
      api.userOrgAccess.list(editingUser.id)
        .then((res) => {
          const grants = res?.data || [];
          setAccessibleOrgCodes(grants.map((g) => g.org_code).filter(Boolean));
          setGrantIdByCode(Object.fromEntries(grants.map((g) => [g.org_code, g.id])));
        })
        .catch(() => {
          // Rôle non habilité ou service indisponible : le champ reste vide,
          // pas de blocage de l'édition de l'utilisateur pour autant.
          setAccessibleOrgCodes([]);
          setGrantIdByCode({});
        })
        .finally(() => setOrgAccessLoading(false));
    } else {
      setAccessibleOrgCodes([]);
      setGrantIdByCode({});
    }
  }, [editingUser]);

  const toggleAccessibleOrg = (code) => {
    setAccessibleOrgCodes((prev) => (
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]
    ));
  };

  /**
   * Fait converger les octrois user_org_access réels sur la sélection de l'écran :
   * accorde les filiales cochées qui manquent, révoque celles décochées qui
   * existaient. Erreurs individuelles avalées (ex. hors tenant) — signalées via
   * onMessage plutôt que de bloquer l'enregistrement de l'utilisateur, déjà fait.
   */
  const syncOrgAccess = async (userId) => {
    const toGrant = accessibleOrgCodes.filter((code) => !grantIdByCode[code]);
    const toRevoke = Object.entries(grantIdByCode).filter(([code]) => !accessibleOrgCodes.includes(code));
    const failures = [];
    await Promise.all([
      ...toGrant.map((code) =>
        api.userOrgAccess.grant(userId, code, formData.role).catch(() => failures.push(code))),
      ...toRevoke.map(([code, id]) =>
        api.userOrgAccess.revoke(id).catch(() => failures.push(code))),
    ]);
    if (failures.length > 0) {
      onMessage && onMessage({
        title: t('common.messages.errorTitle'),
        message: t('admin.orgAccess.syncPartialError', { orgs: failures.join(', ') }),
        type: 'error',
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = { ...formData };

      // Clean up empty fields
      if (submitData.primary_org_id === '') submitData.primary_org_id = null;
      if (submitData.phone === '') delete submitData.phone;

      // For edit, only include password if it was changed
      if (editingUser && submitData.password === '') {
        delete submitData.password;
      }

      if (editingUser) {
        await api.user.update(editingUser.id, submitData);
        await syncOrgAccess(editingUser.id);
        onMessage && onMessage({ title: t('common.messages.successTitle'), message: t('settings.userForm.successUpdate'), type: 'success' });
      } else {
        if (!submitData.password) {
          onMessage && onMessage({ title: t('common.messages.errorTitle'), message: t('settings.userForm.passwordRequiredError'), type: 'error' });
          return;
        }
        const created = await api.user.create(submitData);
        const newUserId = created?.data?.id;
        if (newUserId && accessibleOrgCodes.length > 0) {
          await syncOrgAccess(newUserId);
        }
        onMessage && onMessage({ title: t('common.messages.successTitle'), message: t('settings.userForm.successCreate'), type: 'success' });
      }
      onSuccess();
    } catch (error) {
      onMessage && onMessage({ title: t('common.messages.errorTitle'), message: t('settings.userForm.errorPrefix', { message: error.message }), type: 'error' });
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
          <h2 className="text-2xl font-bold text-gray-800">
            {editingUser ? t('settings.userForm.editTitle') : t('settings.userForm.createTitle')}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition">
            <X className="w-6 h-6 text-gray-600" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Name */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">{t('settings.userForm.firstNameLabel')}</label>
              <input
                type="text"
                required
                value={formData.first_name}
                onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500"
                placeholder={t('settings.userForm.firstNamePlaceholder')}
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">{t('settings.userForm.lastNameLabel')}</label>
              <input
                type="text"
                required
                value={formData.last_name}
                onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500"
                placeholder={t('settings.userForm.lastNamePlaceholder')}
              />
            </div>
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">{t('settings.userForm.emailLabel')}</label>
            <input
              type="email"
              required
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500"
              placeholder={t('settings.userForm.emailPlaceholder')}
            />
          </div>

          {/* Password */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              {t('settings.userForm.passwordLabel')} {!editingUser && '*'}
              {editingUser && <span className="text-gray-400 font-normal">{t('settings.userForm.passwordEditHint')}</span>}
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                required={!editingUser}
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500 pr-12"
                placeholder={editingUser ? t('settings.userForm.passwordPlaceholderEdit') : t('settings.userForm.passwordPlaceholderNew')}
                minLength={6}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {/* Phone */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">{t('settings.userForm.phoneLabel')}</label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500"
              placeholder={t('settings.userForm.phonePlaceholder')}
            />
          </div>

          {/* Role and Organization */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">{t('settings.userForm.roleLabel')}</label>
              <select
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500"
              >
                <option value="viewer">{t('settings.roles.viewer')}</option>
                <option value="driver">{t('settings.roles.driver')}</option>
                <option value="mechanic">{t('settings.roles.mechanic')}</option>
                <option value="manager">{t('settings.roles.manager')}</option>
                <option value="admin">{t('settings.roles.admin')}</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">{t('settings.userForm.orgLabel')}</label>
              <select
                value={formData.primary_org_id}
                onChange={(e) => setFormData({ ...formData, primary_org_id: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500"
              >
                <option value="">{t('settings.userForm.orgNoneOption')}</option>
                {organizations.map(org => (
                  <option key={org.id} value={org.code}>{org.name} ({org.code})</option>
                ))}
              </select>
            </div>
          </div>

          {/* Filiales accessibles (chantier Groupe multi-filiales) */}
          {organizations.filter(org => org.code !== formData.primary_org_id).length > 0 && (
            <div>
              <label className="flex items-center text-sm font-semibold text-gray-700 mb-2">
                <Building2 className="w-4 h-4 mr-1.5 text-gray-500" />
                {t('admin.orgAccess.label')}
              </label>
              <p className="text-xs text-gray-500 mb-2">{t('admin.orgAccess.helper')}</p>
              {orgAccessLoading ? (
                <div className="text-sm text-gray-400">{t('common.states.loading')}</div>
              ) : (
                <div className="border rounded-lg divide-y max-h-48 overflow-y-auto">
                  {organizations
                    .filter(org => org.code !== formData.primary_org_id)
                    .map(org => (
                      <label key={org.id} className="flex items-center px-4 py-2 hover:bg-gray-50 cursor-pointer text-sm">
                        <input
                          type="checkbox"
                          checked={accessibleOrgCodes.includes(org.code)}
                          onChange={() => toggleAccessibleOrg(org.code)}
                          className="mr-3 h-4 w-4 rounded border-gray-300 text-amber-500 focus:ring-amber-500"
                        />
                        <span className="text-gray-900">{org.name}</span>
                        <span className="ml-2 text-gray-400 font-mono text-xs">({org.code})</span>
                      </label>
                    ))}
                </div>
              )}
            </div>
          )}

          {/* Status */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">{t('settings.userForm.statusLabel')}</label>
            <select
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500"
            >
              <option value="active">{t('settings.userForm.userStatus.active')}</option>
              <option value="suspended">{t('settings.userForm.userStatus.suspended')}</option>
              <option value="inactive">{t('settings.userForm.userStatus.inactive')}</option>
            </select>
          </div>

          {/* Role Description */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="font-semibold text-gray-700 mb-2">{t('settings.userForm.permissionsTitle')}</h4>
            <ul className="text-sm text-gray-600 space-y-1">
              {formData.role === 'viewer' && (
                <>
                  <li>- {t('settings.userForm.permissions.viewer.line1')}</li>
                  <li>- {t('settings.userForm.permissions.viewer.line2')}</li>
                </>
              )}
              {formData.role === 'driver' && (
                <>
                  <li>- {t('settings.userForm.permissions.driver.line1')}</li>
                  <li>- {t('settings.userForm.permissions.driver.line2')}</li>
                  <li>- {t('settings.userForm.permissions.driver.line3')}</li>
                </>
              )}
              {formData.role === 'mechanic' && (
                <>
                  <li>- {t('settings.userForm.permissions.mechanic.line1')}</li>
                  <li>- {t('settings.userForm.permissions.mechanic.line2')}</li>
                </>
              )}
              {formData.role === 'manager' && (
                <>
                  <li>- {t('settings.userForm.permissions.manager.line1')}</li>
                  <li>- {t('settings.userForm.permissions.manager.line2')}</li>
                  <li>- {t('settings.userForm.permissions.manager.line3')}</li>
                </>
              )}
              {formData.role === 'admin' && (
                <>
                  <li>- {t('settings.userForm.permissions.admin.line1')}</li>
                  <li>- {t('settings.userForm.permissions.admin.line2')}</li>
                  <li>- {t('settings.userForm.permissions.admin.line3')}</li>
                </>
              )}
            </ul>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-4 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
            >
              {t('common.actions.cancel')}
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg font-semibold transition"
            >
              {editingUser ? t('common.actions.update') : t('common.actions.create')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default UserFormModal;
